/**
 * Shop Community Card Web Component
 * ---------------------------------
 * Visually matches <fragno-community-card> (community badge + title + subtitle)
 * but is NOT a clickable card — no hover/pressed states, no link wrapper.
 * A child element with `slot="cta"` renders as the footer action
 * (a <fragno-button>, a <form>, etc.). The column layout stretches it; a
 * <fragno-button> also needs `full-width` (reskin/upstream/_button.scss) so the
 * host and its inner button span the column.
 *
 * Renders in Light DOM for SEO and for style overrides from page SCSS.
 *
 * Implementation note: the header markup is *prepended* (rather than replacing
 * innerHTML) so slotted children are preserved regardless of whether
 * connectedCallback fires before or after the HTML parser appends them.
 *
 * The badge is optional: omit BOTH community-icon-src and community-label and the card
 * renders badge-less (title + subtitle + CTA only). The store bento box relies on this to
 * render non-community deals through the same card — do not collapse the guard in render().
 *
 * Attributes:
 * - community-icon-src: URL/path to the community badge SVG (optional; see above)
 * - community-icon-alt: Alt text for the icon (optional; decorative by default)
 * - community-label: Community display name ("Military", "Nurses", etc.) (optional; see above)
 * - title: Primary headline
 * - subtitle: Supporting text (optional)
 *
 * Example:
 *   <shop-community-card
 *     community-icon-src="/assets/reskin/icons/communities/military.svg"
 *     community-label="Military"
 *     title="10% off"
 *     subtitle="Plus 2% cash back">
 *     <fragno-button slot="cta" hierarchy="primary" size="lg" full-width
 *                    label="Claim deal" action-href="..."></fragno-button>
 *   </shop-community-card>
 */

import { escapeHTML } from "../../../shared/escape_html.js";

const ShopCommunityCard = (function () {
  const TAG_NAME = "shop-community-card";
  const CONTENT_CLASS = "shop-community-card__content";

  class ShopCommunityCard extends HTMLElement {
    static get observedAttributes() {
      return [
        "community-icon-src",
        "community-icon-alt",
        "community-label",
        "title",
        "subtitle",
      ];
    }

    connectedCallback() {
      this.render();
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      if (oldValue === newValue) return;
      this.render();
    }

    render() {
      const iconSrc = this.getAttribute("community-icon-src") || "";
      const iconAlt = escapeHTML(this.getAttribute("community-icon-alt") || "");
      const label = escapeHTML(this.getAttribute("community-label") || "");
      const title = escapeHTML(this.getAttribute("title") || "");
      const subtitle = escapeHTML(this.getAttribute("subtitle") || "");

      const badgeHtml =
        iconSrc || label
          ? `<div class="shop-community-card__badge">
               ${iconSrc ? `<img class="shop-community-card__badge-icon" src="${escapeHTML(iconSrc)}" alt="${iconAlt}" />` : ""}
               ${label ? `<span class="shop-community-card__badge-label">${label}</span>` : ""}
             </div>`
          : "";

      const subtitleHtml = subtitle
        ? `<p class="shop-community-card__subtitle">${subtitle}</p>`
        : "";

      const contentHtml = `
        <div class="${CONTENT_CLASS}">
          ${badgeHtml}
          ${title ? `<h3 class="shop-community-card__title">${title}</h3>` : ""}
          ${subtitleHtml}
        </div>
      `;

      const existing = this.querySelector(`:scope > .${CONTENT_CLASS}`);
      if (existing) {
        existing.outerHTML = contentHtml;
      } else {
        this.insertAdjacentHTML("afterbegin", contentHtml);
      }
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopCommunityCard);
  }

  return ShopCommunityCard;
})();

export { ShopCommunityCard };
